import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { theme } from '../../styles/theme';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@mui/material';
import PackageSelection from './PackageSelection';
import BusinessInformationStep from './BusinessInformationStep';
import ReviewAndBillingStep from './ReviewAndBillingStep';
import Header from '../layout/Header';
import { createAccount } from '../../services/accountSetup';
import { hasConfirmedFirstPayment } from '../../services/accountSetup/provisionPaidAccount';

const FlowContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  overflow-x: hidden;
  width: 100%;
`;

const MainContent = styled.div`
  flex: 1;
  padding: ${theme.spacing.md};
  padding-top: calc(72px + env(safe-area-inset-top, 0px) + ${theme.spacing.md});
  
  @media (max-width: 768px) {
    padding: ${theme.spacing.sm};
    padding-top: calc(72px + env(safe-area-inset-top, 0px) + ${theme.spacing.sm});
  }

  .MuiAlert-root {
    margin: ${theme.spacing.md} auto;
    max-width: 600px;
    
    @media (max-width: 768px) {
      margin: ${theme.spacing.sm} auto;
      width: calc(100% - ${theme.spacing.md});
    }
  }
`;

const SETUP_DATA_KEY = 'accountSetupData';
const POST_SIGNUP_LOGIN_MESSAGE =
  'Your account was created successfully. Check your email for a verification link, then sign in once your email is verified.';

const AccountSetupFlow = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [setupData, setSetupData] = useState(() => {
    // Try to load saved data from localStorage on initial render
    const savedData = localStorage.getItem(SETUP_DATA_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        return {
          ...parsed,
          // Ensure we have default values for any missing fields
          package: parsed.package || null,
          packageDetails: parsed.packageDetails || null,
          addons: parsed.addons || null,
          total: parsed.total || 0,
          businessInfo: parsed.businessInfo || null,
          billingInfo: parsed.billingInfo || null,
          billingCycle: parsed.billingCycle || 'monthly',
          interval: parsed.interval || 'month',
          isYearly: parsed.isYearly || false,
          subscription: parsed.subscription || null
        };
      } catch (e) {
        console.error('Error parsing saved setup data:', e);
        return {
          package: null,
          packageDetails: null,
          addons: null,
          total: 0,
          businessInfo: null,
          billingInfo: null,
          billingCycle: 'monthly',
          interval: 'month',
          isYearly: false,
          subscription: null
        };
      }
    }
    return {
      package: null,
      packageDetails: null,
      addons: null,
      total: 0,
      businessInfo: null,
      billingInfo: null,
      billingCycle: 'monthly',
      interval: 'month',
      isYearly: false,
      subscription: null
    };
  });

  // Save setup data whenever it changes
  useEffect(() => {
    if (setupData) {
      localStorage.setItem(SETUP_DATA_KEY, JSON.stringify(setupData));
    }
  }, [setupData]);

  useEffect(() => {
    const scrollingElement = document.scrollingElement || document.documentElement;

    const resetScrollPosition = () => {
      if (scrollingElement) {
        scrollingElement.scrollTop = 0;
        scrollingElement.scrollLeft = 0;
      }
      if (document.body) {
        document.body.scrollTop = 0;
        document.body.scrollLeft = 0;
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    };

    resetScrollPosition();

    const animationId = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    });

    return () => cancelAnimationFrame(animationId);
  }, [currentStep]);

  // Clear setup data when component unmounts
  useEffect(() => {
    return () => {
      // Only clear if user has completed setup or navigated away
      if (currentStep === 1) {
        localStorage.removeItem(SETUP_DATA_KEY);
      }
    };
  }, [currentStep]);

  const handlePackageSelection = (packageData) => {
    setSetupData(prev => ({
      ...prev,
      package: packageData.package,
      packageDetails: {
        ...packageData.packageDetails,
        limits: packageData.packageDetails?.limits || null
      },
      addons: packageData.addons,
      total: packageData.total,
      billingCycle: packageData.billingCycle,
      interval: packageData.interval,
      isYearly: packageData.isYearly,
      subscription: null
    }));
    setCurrentStep(2);
  };

  const handleBusinessInfo = (businessInfo) => {
    setSetupData(prev => ({
      ...prev,
      businessInfo
    }));
    setCurrentStep(3);
  };

  const handleBillingInfo = async (billingResult) => {
    if (isSubmitting) return;

    try {
      setError('');
      setIsSubmitting(true);
      sessionStorage.setItem('accountSetupInProgress', 'true');
      
      const subscriptionData = billingResult.subscription || setupData.subscription;
      const billingAddress = billingResult.billingAddress || setupData.billingInfo;

      if (!subscriptionData) {
        console.error('Missing subscription data when finalizing account setup');
        throw new Error('We were unable to confirm your subscription. Please refresh the page and try again.');
      }

      const isFreeTrial = setupData.package === 'free_trial' || billingResult.package === 'free_trial';
      if (!isFreeTrial && !hasConfirmedFirstPayment(subscriptionData)) {
        throw new Error('Payment was not confirmed. No account was created.');
      }

      // Update the setup data with billing info and subscription data
      const finalData = {
        ...setupData,
        subscription: subscriptionData,
        billingInfo: billingAddress,
        packageDetails: {
          ...setupData.packageDetails,
          limits: setupData.packageDetails?.limits || null
        }
      };

      // Persist subscription and billing info for potential retries without storing payment details
      setSetupData(prev => ({
        ...prev,
        billingInfo: billingAddress,
        subscription: subscriptionData
      }));
      
      // Create the account
      await createAccount(finalData);

      localStorage.removeItem(SETUP_DATA_KEY);
      sessionStorage.removeItem('accountSetupInProgress');

      const stillOnSetup = window.location.pathname.includes('/main/account-setup');

      if (stillOnSetup) {
        navigate('/app/login', {
          replace: true,
          state: {
            verificationMessage: POST_SIGNUP_LOGIN_MESSAGE,
            signupEmail: finalData.businessInfo?.email || '',
          },
        });
      }
      
    } catch (error) {
      sessionStorage.removeItem('accountSetupInProgress');
      console.error('Error processing setup:', error);
      if (error?.code === 'auth/email-already-in-use' || error?.details?.code === 'EMAIL_ALREADY_IN_USE') {
        setError('An account with this email already exists. Please log in or contact our support team if you need help completing your setup.');
      } else if (error?.details?.code === 'CARD_DECLINED') {
        setError('Your card was declined. No account was created.');
      } else {
        setError(error.message || 'An error occurred while creating your account. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <PackageSelection 
          onNext={handlePackageSelection} 
          initialData={setupData.package ? {
            package: setupData.package,
            packageDetails: setupData.packageDetails,
            addons: setupData.addons,
            total: setupData.total,
            billingCycle: setupData.billingCycle,
            interval: setupData.interval,
            isYearly: setupData.isYearly
          } : null}
        />;
      case 2:
        return (
          <BusinessInformationStep 
            onNext={handleBusinessInfo}
            onBack={() => setCurrentStep(1)}
            initialData={setupData.businessInfo}
          />
        );
      case 3:
        return (
          <ReviewAndBillingStep 
            onNext={handleBillingInfo}
            onBack={() => {
              setSetupData(prev => ({
                ...prev,
                subscription: null
              }));
              setCurrentStep(2);
            }}
            total={setupData.total}
            packageData={{
              id: setupData.package,
              packageDetails: setupData.packageDetails,
              addons: setupData.addons
            }}
            businessData={setupData.businessInfo}
            billingCycle={setupData.billingCycle}
            interval={setupData.interval}
            isYearly={setupData.isYearly}
            initialBillingAddress={setupData.billingInfo}
            existingSubscription={setupData.subscription}
            onClearSubscription={() => {
              setSetupData(prev => ({
                ...prev,
                subscription: null,
              }));
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <FlowContainer>
      <Header />
      <MainContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3, mx: 3 }}>
            {error}
          </Alert>
        )}
        {renderCurrentStep()}
      </MainContent>
    </FlowContainer>
  );
};

export default AccountSetupFlow; 